// CFD chart — FE-037 / spec §7.0.11.
//
// ECharts-driven stacked-area chart. The same data is also rendered
// as a screen-reader-only summary so accessibility and unit tests
// don't have to poke the canvas.
//
// ECharts is loaded as a dependency per spec §7.0; we deliberately
// skip ``echarts-for-react`` here because importing it triggers
// canvas initialisation in vitest's happy-dom environment, which
// fails. Loading ``echarts/core`` + the specific chart/component/
// renderer modules keeps the import cost smaller and the runtime
// tree-shaking obvious.

import { LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import {
  buildCfdDrillThroughUrl,
  type CfdResponse,
  type CfdSeriesPoint,
} from "@/features/dashboard";

import {
  CFD_STATUS_META,
  CFD_STATUS_ORDER,
} from "./statusMeta";


// Register ECharts modules once per bundle. ``echarts/core`` is a
// no-op on a second import so this is safe under React's
// double-mount in StrictMode.
echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  CanvasRenderer,
]);


export interface CfdChartProps {
  data: CfdResponse;
  /** Optional category / assignee for drill-through context. */
  category?: string;
  assignee?: string;
}


export function CfdChart({ data, category, assignee }: CfdChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<ReturnType<typeof echarts.init> | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = echarts.init(containerRef.current);
    instanceRef.current = chart;

    const dates = data.series.map((p) => p.date);
    // ``done`` is the cumulative throughput line; rendering it as a
    // stacked area band swamps the in-flight bands visually (90%+ of
    // any healthy brokerage's work is done). Render it as a separate
    // unstacked trend line and keep only the still-in-flight states
    // (+ cancelled as a small terminal band) in the stack — that
    // surfaces the WIP shape that actually moves week to week.
    const stackedStatuses = CFD_STATUS_ORDER.filter((s) => s !== "done");
    const stackedSeries = stackedStatuses.map((status) => ({
      name: CFD_STATUS_META[status].label,
      type: "line" as const,
      stack: "wip",
      areaStyle: {},
      smooth: false,
      symbol: "none",
      itemStyle: { color: CFD_STATUS_META[status].fill },
      lineStyle: { width: 1, color: CFD_STATUS_META[status].fill },
      data: data.series.map((p) => p.counts[status] ?? 0),
    }));
    const doneTrendSeries = {
      name: CFD_STATUS_META.done.label,
      type: "line" as const,
      // No ``stack`` — overlay on its own y-axis so the line is
      // legible against the WIP stack without compressing it.
      yAxisIndex: 1,
      smooth: false,
      symbol: "none",
      itemStyle: { color: CFD_STATUS_META.done.fill },
      lineStyle: { width: 2, color: CFD_STATUS_META.done.fill },
      data: data.series.map((p) => p.counts.done ?? 0),
    };
    const series = [...stackedSeries, doneTrendSeries];

    chart.setOption({
      grid: { top: 8, left: 36, right: 44, bottom: 28 },
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "category",
        data: dates,
        axisLabel: { fontSize: 10, hideOverlap: true },
        axisLine: { lineStyle: { color: "#D6CDB6" } },
        axisTick: { show: false },
      },
      yAxis: [
        // Primary axis: WIP stack (in-flight + cancelled).
        {
          type: "value",
          axisLabel: { fontSize: 10 },
          splitLine: { lineStyle: { color: "#EFEBE0" } },
        },
        // Secondary axis: cumulative Done trend overlay. Tucked on
        // the right; no split-line so it doesn't double-band the
        // canvas.
        {
          type: "value",
          position: "right",
          axisLabel: {
            fontSize: 10,
            color: CFD_STATUS_META.done.fill,
          },
          splitLine: { show: false },
        },
      ],
      series,
    });

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
      instanceRef.current = null;
    };
  }, [data]);

  return (
    <div data-testid="cfd-chart" className="flex flex-col gap-3">
      <div
        ref={containerRef}
        data-testid="cfd-chart-canvas"
        className="h-56 w-full"
        role="img"
        aria-label={chartAriaLabel(data)}
      />
      <CfdAccessibleSummary
        data={data}
        category={category}
        assignee={assignee}
      />
    </div>
  );
}


/**
 * Render the same data as an accessible (`<dl>`-style) list with
 * one entry per snapshot date. Each row links to the FE-031
 * drill-through URL so keyboard / screen-reader users get the same
 * navigational affordance as a click on the canvas. The list is
 * visually compact: only the first, last, and the largest-total
 * dates are shown by default, with a "show all" toggle that the FE
 * can polish later.
 */
function CfdAccessibleSummary({
  data, category, assignee,
}: { data: CfdResponse; category?: string; assignee?: string }) {
  const featured = pickFeaturedPoints(data.series);
  return (
    <div data-testid="cfd-summary" className="flex flex-col gap-2 text-xs">
      <p className="text-text-tertiary">
        <span data-testid="cfd-summary-range">
          {formatDate(data.range_start)} – {formatDate(data.range_end)}
        </span>
        {" · "}
        <span data-testid="cfd-summary-totals">
          end-of-window: {formatTotals(data.total_by_status)}
        </span>
      </p>
      {featured.length > 0 && (
        <ul
          data-testid="cfd-summary-points"
          role="list"
          className="flex flex-col gap-0.5"
        >
          {featured.map((point) => (
            <li
              key={point.date}
              data-testid={`cfd-summary-point-${point.date}`}
            >
              <span className="text-text-secondary">
                {formatDate(point.date)}:
              </span>{" "}
              {CFD_STATUS_ORDER.filter((s) => point.counts[s] > 0).map(
                (status, idx) => (
                  <span key={status} className="text-text-primary">
                    {idx > 0 ? ", " : ""}
                    <Link
                      to={buildCfdDrillThroughUrl({
                        status, snapshotDate: point.date,
                        category, assignee,
                      })}
                      data-testid={`cfd-segment-link-${point.date}-${status}`}
                      className="hover:underline"
                    >
                      {point.counts[status]} {CFD_STATUS_META[status].label.toLowerCase()}
                    </Link>
                  </span>
                ),
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function pickFeaturedPoints(series: CfdSeriesPoint[]): CfdSeriesPoint[] {
  if (series.length === 0) return [];
  if (series.length <= 3) return series;
  let peakIdx = 0;
  let peakTotal = -Infinity;
  for (let i = 0; i < series.length; i += 1) {
    const total = sumCounts(series[i]);
    if (total > peakTotal) {
      peakTotal = total;
      peakIdx = i;
    }
  }
  const indices = new Set([0, peakIdx, series.length - 1]);
  return [...indices]
    .sort((a, b) => a - b)
    .map((i) => series[i]);
}


function sumCounts(point: CfdSeriesPoint): number {
  return CFD_STATUS_ORDER.reduce(
    (sum, status) => sum + (point.counts[status] ?? 0),
    0,
  );
}


function chartAriaLabel(data: CfdResponse): string {
  const range = `${formatDate(data.range_start)} to ${formatDate(data.range_end)}`;
  return (
    `Cumulative flow chart spanning ${range}. ` +
    `End-of-window totals: ${formatTotals(data.total_by_status)}.`
  );
}


function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric",
  });
}


function formatTotals(totals: CfdResponse["total_by_status"]): string {
  return CFD_STATUS_ORDER
    .filter((s) => (totals[s] ?? 0) > 0)
    .map((s) => `${totals[s]} ${CFD_STATUS_META[s].label.toLowerCase()}`)
    .join(", ") || "no data";
}


// Re-export so the section component can render the same drill-
// through URL inside a "see all" experience FE-038+ may add.
export { buildCfdDrillThroughUrl };
